// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
'use client';
import React from 'react';

import { getAnalyticsMetadataAttribute } from '@amzn/awsui-component-toolkit/internal/analytics-metadata';

import { AnalyticsFunnelSubStep } from '../internal/analytics/components/analytics-funnel';
import { BasePropsWithAnalyticsMetadata, getAnalyticsMetadataProps } from '../internal/base-component';
import useBaseComponent from '../internal/hooks/use-base-component';
import { applyDisplayName } from '../internal/utils/apply-display-name';
import { getExternalProps } from '../internal/utils/external-props';
import { GeneratedAnalyticsMetadataContainerComponent } from './analytics-metadata/interfaces';
import { ContainerProps } from './interfaces';
import { InternalContainerAsSubstep } from './internal';

export { ContainerProps };

const Container = React.forwardRef(
  (
    {
      variant = 'default',
      disableHeaderPaddings = false,
      disableContentPaddings = false,
      fitHeight = false,
      ...props
    }: ContainerProps,
    ref: React.Ref<ContainerProps.Ref>
  ) => {
    const analyticsMetadata = getAnalyticsMetadataProps(props as BasePropsWithAnalyticsMetadata);
    const baseComponentProps = useBaseComponent(
      'Container',
      {
        props: {
          disableContentPaddings,
          disableHeaderPaddings,
          fitHeight,
          variant,
        },
        metadata: {
          hasInstanceIdentifier: Boolean(analyticsMetadata?.instanceIdentifier),
        },
      },
      analyticsMetadata
    );
    const externalProps = getExternalProps(props);

    const analyticsComponentMetadata: GeneratedAnalyticsMetadataContainerComponent = {
      name: 'awsui.Container',
      label: { root: 'self' },
    };

    return (
      <AnalyticsFunnelSubStep
        subStepIdentifier={analyticsMetadata?.instanceIdentifier}
        subStepErrorContext={analyticsMetadata?.errorContext}
      >
        <InternalContainerAsSubstep
          ref={ref}
          variant={variant}
          disableContentPaddings={disableContentPaddings}
          disableHeaderPaddings={disableHeaderPaddings}
          fitHeight={fitHeight}
          {...props}
          {...externalProps}
          {...baseComponentProps}
          {...getAnalyticsMetadataAttribute({ component: analyticsComponentMetadata })}
        />
      </AnalyticsFunnelSubStep>
    );
  }
);

applyDisplayName(Container, 'Container');
export default Container;



// WEBPACK FOOTER //
// /local/p4clients/pkgbuild-const/src/container/index.tsx